import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";

/// Css
import "./index.css";
import "./chart.css";
import "./step.css";

/// Layout
import Nav from "./layouts/nav";
import ScrollToTop from "./layouts/ScrollToTop";
import Footer from "./layouts/Footer";

/// Pages
import Registration from "./pages/Registration";
import Login from "./pages/Login";

/// Dashboard
import Home from "./components/Dashboard/Home";
import DashboardDark from "./components/Dashboard/DashboardDark";
import Doctors from "./components/Dashboard/Doctors";
import DoctorsDetails from "./components/Dashboard/Doctors-Details";
import DoctorsReview from "./components/Dashboard/Doctors-Review";
import PatientDetails from "./components/Dashboard/Patient-Details";
import FlatIcon from "./components/Dashboard/FlatIcon";

/// CMS
import Content from "./components/Cms/Content";
import Services from "./components/Cms/Services";
import About from "./components/Cms/About";
import WhyChooseUs from "./components/Cms/WhyChooseUs";
import Contact from "./components/Cms/Contact";
import Seo from "./components/Cms/Seo";
import Banner from "./components/Cms/Banner";
import Menu from "./components/Cms/Menu";
import EmailTemplate from "./components/Cms/EmailTemplate";
import Blog from "./components/Cms/Blog";
import ContentAdd from "./components/Cms/ContentAdd";
import AddMail from "./components/Cms/AddMail";
import AddBlog from "./components/Cms/AddBlog";
import BlogCategory from "./components/Cms/BlogCategory";
import Roles from "./components/Cms/Roles";
import ImageAlt from "./components/Cms/ImageAlt";

/// Table / Forms / Plugins
import BootstrapTable from "./components/table/BootstrapTable";
import Element from "./components/Forms/Element/Element";
import Wizard from "./components/Forms/Wizard/Wizard";
import Lightgallery from "./components/PluginsMenu/Lightgallery/Lightgallery";

import { ThemeContext } from "../context/ThemeContext";

/// AUTH UTILS
import { getToken, getRole, getHomeRouteByRole, hasPageAccess, getAllowedPages } from "../utils/auth";

const PrivateRoute = ({ children, pageKey = null }) => {
  const token = getToken();
  const role = getRole();
  const allowedPages = getAllowedPages();

  if (!token || !role) {
    return <Navigate to="/page-login" replace />;
  }

  if (pageKey && role !== "admin" && !hasPageAccess(pageKey)) {
    return (
      <Navigate
        to={getHomeRouteByRole(role, allowedPages)}
        replace
      />
    );
  }

  return children;
};

/// ================= MAIN APP =================
const Markup = () => {
  const token = getToken();
  const role = getRole();

  const allroutes = [
  { url: "", component: <Home />, pageKey: "dashboard" },
  { url: "dashboard", component: <Home />, pageKey: "dashboard" },
  { url: "index-2", component: <DashboardDark />, pageKey: "dashboard" },
  { url: "doctors", component: <Doctors />, pageKey: "dashboard" },
  { url: "doctors-details", component: <DoctorsDetails />, pageKey: "dashboard" },
  { url: "doctors-review", component: <DoctorsReview />, pageKey: "dashboard" },
  { url: "patient-details", component: <PatientDetails />, pageKey: "dashboard" },
  { url: "flat-icons", component: <FlatIcon />, pageKey: "dashboard" },

  { url: "services", component: <Services />, pageKey: "services" },
  { url: "about", component: <About />, pageKey: "about" },
  { url: "why-choose-us", component: <WhyChooseUs />, pageKey: "why-choose-us" },
  { url: "contact", component: <Contact />, pageKey: "contact" },
  { url: "seo", component: <Seo />, pageKey: "seo" },
  { url: "banner", component: <Banner />, pageKey: "banner" },
  { url: "menu", component: <Menu />, pageKey: "dashboard" },
  { url: "roles", component: <Roles />, pageKey: "roles" },
  { url: "email-template", component: <EmailTemplate />, pageKey: "email-template" },
  { url: "blog", component: <Blog />, pageKey: "blog" },
  { url: "add-content", component: <ContentAdd />, pageKey: "add-content" },
  { url: "add-email", component: <AddMail />, pageKey: "add-email" },
  { url: "add-blog", component: <AddBlog />, pageKey: "add-blog" },
  { url: "blog-category", component: <BlogCategory />, pageKey: "blog-category" },

  { url: "appointments", component: <BootstrapTable />, pageKey: "appointments" },
  { url: "blogs", component: <Content />, pageKey: "blogs" },
  { url: "testimonials", component: <Element />, pageKey: "testimonials" },
  { url: "faq", component: <Wizard />, pageKey: "faq" },
  { url: "gallery", component: <Lightgallery />, pageKey: "gallery" },
  { url: "imagealt", component: <ImageAlt />, pageKey: "imagealt" },
];

  return (
    <>
      <Routes>
        {/* ================= PUBLIC ROUTES ================= */}
        <Route path="page-login" element={<Login />} />
        <Route path="page-register" element={<Registration />} />

        {/* ================= ROOT REDIRECT ================= */}
        <Route
          path="/"
          element={
            token && role ? (
              <Navigate to={getHomeRouteByRole(role)} replace />
            ) : (
              <Navigate to="/page-login" replace />
            )
          }
        />

        {/* ================= PROTECTED ROUTES ================= */}
        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          {allroutes.map((data, i) => (
  <Route
    key={i}
    path={data.url}
    element={
      <PrivateRoute pageKey={data.pageKey}>
        {data.component}
      </PrivateRoute>
    }
  />
))}
        </Route>

        {/* ================= FALLBACK ================= */}
        <Route
          path="*"
          element={
            token && role ? (
              <Navigate to={getHomeRouteByRole(role)} replace />
            ) : (
              <Navigate to="/page-login" replace />
            )
          }
        />
      </Routes>

      <ScrollToTop />
    </>
  );
};

/// ================= MAIN LAYOUT =================
function MainLayout() {
  const { sidebariconHover } = useContext(ThemeContext);
  const sideMenu = useSelector((state) => state.sideMenu);

  return (
    <div
      id="main-wrapper"
      className={`show ${
        sidebariconHover ? "icon-hover-toggle" : ""
      } ${sideMenu ? "menu-toggle" : ""}`}
    >
      <Nav />

      <div
        className="content-body"
        style={{ minHeight: window.screen.height - 60 }}
      >
        <div className="container-fluid">
          <Outlet />
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Markup;